import sanitizeHtml from "sanitize-html";
import parse from "html-react-parser";
import {
  ArtworkObject,
  ArtWorkRecordsResult,
  StoryItemsResponse,
  StoryItemType,
  StoryResponse,
} from "../types/payloadTypes";

export const constructResultAndInRoomSlider = (
  artworkResult: ArtWorkRecordsResult,
  isTablet: boolean
): ArtworkObject => {
  const { success } = artworkResult;

  let artwork = {};

  if (success) {
    // If the artwork result has records
    if (artworkResult["data"]["records"].length > 0) {
      // Extract needed data from the art object
      const artObject = artworkResult["data"]["records"][0];
      const {
        id,
        title,
        shortDescription,
        people: artist,
        nationality,
        birthDate,
        deathDate,
        culture,
        classification,
        locations,
        medium,
        invno,
        displayDate,
        dimensions,
        visualDescription,
        content,
        creditLine,
      } = artObject;

      // Determine the flags
      const curatorialApproval =
        artObject.curatorialApproval === "false" ? false : true;
      const unIdentified = (artObject.people || "")
        .toLowerCase()
        .includes("unidentified");

      // Prefer the static CloudFront pre-sized variant (cdn_url) over the
      // Imgix-transformed URL. CloudFront serves a single 320w JPEG with no
      // transform overhead — same URL used twice in the result page (once
      // as the focused image, once as the CSS-blurred background).
      const imageUrl = artObject.cdn_url || artObject.art_url;

      // Assign into artwork
      artwork = {
        id,
        title,
        shortDescription,
        artist,
        nationality,
        birthDate,
        deathDate,
        culture,
        classification,
        locations,
        medium,
        invno,
        displayDate,
        dimensions,
        visualDescription,
        content,
        creditLine,

        // Set the urls — same image used for main + bg (CSS handles the blur)
        url: imageUrl,
        url_low_quality: imageUrl,
        bg_url: imageUrl,

        // Set the flags
        curatorialApproval,
        unIdentified,
      };
    }
  }
  return artwork;
};

export const constructStory = (
  storyInformation: StoryItemsResponse
): StoryResponse => {
  let stories: StoryItemType[] = [],
    storyId: string = undefined,
    storyTitle: string = undefined;

  if (storyInformation.data.total > 0) {
    ({ stories, story_title: storyTitle } = storyInformation.data.content);
    storyId = storyInformation.data.unique_identifier;
  }

  return { stories, storyId, storyTitle };
};

const RICH_TEXT_OPTS: sanitizeHtml.IOptions = {
  allowedTags: [
    "h1",
    "h2",
    "h3",
    "h4",
    "h5",
    "h6",
    "blockquote",
    "li",
    "ol",
    "p",
    "ul",
    "a",
    "br",
    "code",
    "em",
    "span",
    "strong",
    "u",
    "table",
    "tbody",
    "td",
    "tfoot",
    "th",
    "thead",
    "tr",
  ],
  allowedAttributes: {
    a: ["href", "target"],
  },
};

export const formatHtml = (html) => {
  return parse(sanitizeHtml(html, RICH_TEXT_OPTS));
};

/** FOC-255: the same allow-list as formatHtml, returned as a string for
 *  dangerouslySetInnerHTML (e.g. shortDescription, which can now come from
 *  authored exhibition records, not only the catalog). */
export const sanitizeRichText = (html?: string | null): string =>
  sanitizeHtml(html || "", RICH_TEXT_OPTS);

export const formatHtmlCaption = (html) => {
  return parse(
    sanitizeHtml(html, {
      allowedTags: ["p", "a", "br", "em", "span", "strong", "u"],
      allowedAttributes: {
        a: ["href", "target"],
      },
    })
  );
};

export const formatHtmlTitle = (html) => {
  return parse(
    sanitizeHtml(html, {
      allowedTags: ["p", "br", "em", "span", "strong", "u"],
      allowedAttributes: {
        a: ["href", "target"],
      },
    })
  );
};
